import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { HeaderLayoutPicker } from "../HeaderLayoutPicker";
import { HeaderTextEditor } from "../HeaderTextEditor";
import type { PrintLayoutFormValues } from "../printLayoutForm";
import type { ImageUploader } from "../useImageUpload";
import { ImageField } from "./ImageField";
import { PanelShell } from "./PanelShell";

export function HeaderPanel({ images, variant = "desktop" }: { images: ImageUploader; variant?: "desktop" | "mobile" }) {
  const { t } = useTranslation("settings");
  const { watch, setValue } = useFormContext<PrintLayoutFormValues>();
  const layout = watch("headerLayout");
  const logoHeight = watch("headerLogoHeightPx");
  const showLogoSize = layout === "inline" || layout === "inline_apart" || layout === "center";

  return (
    <PanelShell title={t("printEditor.areas.header")}>
      <div className="flex flex-col gap-2">
        <Label id="print-header-layout">{t("printEditor.header.layout")}</Label>
        <HeaderLayoutPicker variant={variant} labelledBy="print-header-layout" value={layout} onChange={(next) => setValue("headerLayout", next, { shouldDirty: true })} />
      </div>

      <ImageField
        kind="header"
        label={layout === "full" ? t("printEditor.header.image") : t("printEditor.header.logo")}
        url={watch("headerImageUrl")}
        images={images}
        onClear={() => {
          setValue("headerImageUrl", "", { shouldDirty: true });
          images.clearPending("header");
        }}
      />

      {showLogoSize ? (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <Label id="print-logo-size">{t("printEditor.header.logoSize")}</Label>
            <span className="text-xs text-muted-foreground">{logoHeight}px</span>
          </div>
          <Slider
            aria-labelledby="print-logo-size"
            min={32}
            max={120}
            step={2}
            value={[logoHeight]}
            onValueChange={([next]) => setValue("headerLogoHeightPx", next, { shouldDirty: true })}
          />
        </div>
      ) : null}

      {layout !== "full" ? (
        <HeaderTextEditor value={watch("headerHtml")} onChange={(html) => setValue("headerHtml", html, { shouldDirty: true })} />
      ) : null}
    </PanelShell>
  );
}
